// src/hooks/useAssets.js
// ============================================================
// Asset Management Hooks & Helpers
// Firestore collection: "assets"
// Audit trail collection: "assetHistory"
// ============================================================

import { useState, useEffect } from "react";
import {
  collection,
  doc,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config.js";

// ── Auto-generate asset tag ──────────────────────────────────
async function generateAssetTag() {
  const snap = await getDocs(collection(db, "assets"));
  const num = snap.size + 1;
  return `AST-${String(num).padStart(4, "0")}`;
}

// ── Write audit log entry ────────────────────────────────────
async function writeAssetHistory({
  assetId,
  assetTag,
  assetName,
  action,
  userProfile,
  assignedTo = null,
  assignedToName = null,
  notes = "",
}) {
  try {
    await addDoc(collection(db, "assetHistory"), {
      assetId,
      assetTag,
      assetName,
      action,
      performedBy: userProfile?.uid || "system",
      performedByName:
        userProfile?.displayName || userProfile?.name || "Admin/HR",
      performedByRole: userProfile?.role || "admin",
      assignedTo: assignedTo || null,
      assignedToName: assignedToName || null,
      notes,
      timestamp: serverTimestamp(),
    });
  } catch (err) {
    console.error("[writeAssetHistory]", err);
  }
}

// ── HOOKS ────────────────────────────────────────────────────

/**
 * useAssets — real-time listener for all assets, sorted by createdAt desc.
 */
export function useAssets() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "assets"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("[useAssets]", err.code);
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  return { data, loading };
}

/**
 * useMyAssets — assets currently assigned to a specific employee.
 */
export function useMyAssets(userId) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setData([]);
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, "assets"),
      where("assignedTo", "==", userId),
      where("status", "==", "assigned")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("[useMyAssets]", err.code);
        setLoading(false);
      }
    );
    return unsub;
  }, [userId]);

  return { data, loading };
}

/**
 * useAssetHistory — real-time audit history for a single asset, desc order.
 */
export function useAssetHistory(assetId) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!assetId) {
      setData([]);
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, "assetHistory"),
      where("assetId", "==", assetId),
      orderBy("timestamp", "desc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("[useAssetHistory]", err.code);
        setLoading(false);
      }
    );
    return unsub;
  }, [assetId]);

  return { data, loading };
}

// ── WRITE HELPERS ────────────────────────────────────────────

/**
 * createAsset — add a new asset with audit trail.
 */
export async function createAsset(
  {
    name,
    category,
    brand = "",
    model = "",
    serialNumber = "",
    purchaseDate = "",
    warrantyExpiry = "",
    notes = "",
  },
  userProfile = null
) {
  const assetTag = await generateAssetTag();

  const auditInfo = {
    createdBy: userProfile?.uid || "system",
    createdByName:
      userProfile?.displayName || userProfile?.name || "Admin/HR",
    createdByRole: userProfile?.role || "admin",
  };

  const docRef = await addDoc(collection(db, "assets"), {
    assetTag,
    name,
    category,
    brand,
    model,
    serialNumber,
    purchaseDate,
    warrantyExpiry,
    status: "available",
    assignedTo: null,
    assignedToName: null,
    assignedToEmpId: null,
    assignedAt: null,
    returnedAt: null,
    notes,
    ...auditInfo,
    updatedBy: auditInfo.createdBy,
    updatedByName: auditInfo.createdByName,
    updatedByRole: auditInfo.createdByRole,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  await writeAssetHistory({
    assetId: docRef.id,
    assetTag,
    assetName: name,
    action: "created",
    userProfile,
    notes: `Asset created. Category: ${category}`,
  });

  return docRef;
}

/**
 * updateAsset — edit an existing asset's details.
 */
export async function updateAsset(assetId, updates, userProfile = null) {
  const auditInfo = {
    updatedBy: userProfile?.uid || "system",
    updatedByName:
      userProfile?.displayName || userProfile?.name || "Admin/HR",
    updatedByRole: userProfile?.role || "admin",
  };

  await updateDoc(doc(db, "assets", assetId), {
    ...updates,
    ...auditInfo,
    updatedAt: serverTimestamp(),
  });

  // Fetch the updated doc tag/name for audit
  const snap = await getDocs(
    query(collection(db, "assets"), where("__name__", "==", assetId))
  );
  const assetData = snap.docs[0]?.data() || {};

  await writeAssetHistory({
    assetId,
    assetTag: assetData.assetTag || "",
    assetName: updates.name || assetData.name || "",
    action: "updated",
    userProfile,
    notes: "Asset details updated.",
  });
}

/**
 * assignAsset — assign an asset to an employee.
 * @param {string} assetId
 * @param {{ uid, displayName, employeeId }} employee
 * @param {object} userProfile
 */
export async function assignAsset(assetId, employee, userProfile = null) {
  const auditInfo = {
    updatedBy: userProfile?.uid || "system",
    updatedByName:
      userProfile?.displayName || userProfile?.name || "Admin/HR",
    updatedByRole: userProfile?.role || "admin",
  };

  const snap = await getDocs(
    query(collection(db, "assets"), where("__name__", "==", assetId))
  );
  const assetData = snap.docs[0]?.data() || {};

  if (assetData.status === "assigned") {
    throw new Error("Asset is already assigned to someone.");
  }
  if (assetData.status === "under_maintenance") {
    throw new Error("Asset is under maintenance and cannot be assigned.");
  }
  if (assetData.status === "retired") {
    throw new Error("Retired assets cannot be assigned.");
  }

  await updateDoc(doc(db, "assets", assetId), {
    status: "assigned",
    assignedTo: employee.uid || employee.id,
    assignedToName: employee.displayName || employee.name || "",
    assignedToEmpId: employee.employeeId || "",
    assignedAt: serverTimestamp(),
    returnedAt: null,
    ...auditInfo,
    updatedAt: serverTimestamp(),
  });

  await writeAssetHistory({
    assetId,
    assetTag: assetData.assetTag || "",
    assetName: assetData.name || "",
    action: "assigned",
    userProfile,
    assignedTo: employee.uid || employee.id,
    assignedToName: employee.displayName || employee.name || "",
    notes: `Assigned to ${employee.displayName || employee.name} (${employee.employeeId || ""})`,
  });
}

/**
 * reassignAsset — atomically reassign an asset from one employee to another.
 * Writes a single Firestore document update and a single audit history entry.
 * @param {string} assetId
 * @param {{ uid, displayName, employeeId }} newEmployee
 * @param {object} userProfile  — the admin/HR performing the action
 * @param {string} prevName     — display name of the previous assignee (for the note)
 */
export async function reassignAsset(assetId, newEmployee, userProfile = null, prevName = "") {
  const auditInfo = {
    updatedBy: userProfile?.uid || "system",
    updatedByName:
      userProfile?.displayName || userProfile?.name || "Admin/HR",
    updatedByRole: userProfile?.role || "admin",
  };

  const snap = await getDocs(
    query(collection(db, "assets"), where("__name__", "==", assetId))
  );
  const assetData = snap.docs[0]?.data() || {};

  if (assetData.status === "retired") {
    throw new Error("Retired assets cannot be reassigned.");
  }
  if (assetData.status === "under_maintenance") {
    throw new Error("Asset is under maintenance and cannot be reassigned.");
  }

  await updateDoc(doc(db, "assets", assetId), {
    status: "assigned",
    assignedTo: newEmployee.uid || newEmployee.id,
    assignedToName: newEmployee.displayName || newEmployee.name || "",
    assignedToEmpId: newEmployee.employeeId || "",
    assignedAt: serverTimestamp(),
    returnedAt: null,
    ...auditInfo,
    updatedAt: serverTimestamp(),
  });

  const from = prevName || assetData.assignedToName || "previous employee";
  const to = newEmployee.displayName || newEmployee.name || "";

  await writeAssetHistory({
    assetId,
    assetTag: assetData.assetTag || "",
    assetName: assetData.name || "",
    action: "assigned",
    userProfile,
    assignedTo: newEmployee.uid || newEmployee.id,
    assignedToName: to,
    notes: `Reassigned from ${from} → ${to} (${newEmployee.employeeId || ""})`,
  });
}



/**
 * returnAsset — mark asset as returned and available again.
 */
export async function returnAsset(assetId, userProfile = null, notes = "") {
  const auditInfo = {
    updatedBy: userProfile?.uid || "system",
    updatedByName:
      userProfile?.displayName || userProfile?.name || "Admin/HR",
    updatedByRole: userProfile?.role || "admin",
  };

  const snap = await getDocs(
    query(collection(db, "assets"), where("__name__", "==", assetId))
  );
  const assetData = snap.docs[0]?.data() || {};
  const prevAssigneeName = assetData.assignedToName || "";

  await updateDoc(doc(db, "assets", assetId), {
    status: "available",
    assignedTo: null,
    assignedToName: null,
    assignedToEmpId: null,
    assignedAt: null,
    returnedAt: serverTimestamp(),
    ...auditInfo,
    updatedAt: serverTimestamp(),
  });

  await writeAssetHistory({
    assetId,
    assetTag: assetData.assetTag || "",
    assetName: assetData.name || "",
    action: "returned",
    userProfile,
    notes: notes || `Returned by ${prevAssigneeName}`,
  });
}

/**
 * setAssetMaintenance — put an asset in maintenance mode.
 */
export async function setAssetMaintenance(
  assetId,
  userProfile = null,
  notes = ""
) {
  const auditInfo = {
    updatedBy: userProfile?.uid || "system",
    updatedByName:
      userProfile?.displayName || userProfile?.name || "Admin/HR",
    updatedByRole: userProfile?.role || "admin",
  };

  const snap = await getDocs(
    query(collection(db, "assets"), where("__name__", "==", assetId))
  );
  const assetData = snap.docs[0]?.data() || {};

  await updateDoc(doc(db, "assets", assetId), {
    status: "under_maintenance",
    assignedTo: null,
    assignedToName: null,
    assignedToEmpId: null,
    assignedAt: null,
    ...auditInfo,
    updatedAt: serverTimestamp(),
  });

  await writeAssetHistory({
    assetId,
    assetTag: assetData.assetTag || "",
    assetName: assetData.name || "",
    action: "under_maintenance",
    userProfile,
    notes: notes || "Sent to maintenance.",
  });
}

/**
 * retireAsset — retire an asset permanently (Admin only).
 */
export async function retireAsset(assetId, userProfile = null, notes = "") {
  const auditInfo = {
    updatedBy: userProfile?.uid || "system",
    updatedByName:
      userProfile?.displayName || userProfile?.name || "Admin/HR",
    updatedByRole: userProfile?.role || "admin",
  };

  const snap = await getDocs(
    query(collection(db, "assets"), where("__name__", "==", assetId))
  );
  const assetData = snap.docs[0]?.data() || {};

  await updateDoc(doc(db, "assets", assetId), {
    status: "retired",
    assignedTo: null,
    assignedToName: null,
    assignedToEmpId: null,
    assignedAt: null,
    ...auditInfo,
    updatedAt: serverTimestamp(),
  });

  await writeAssetHistory({
    assetId,
    assetTag: assetData.assetTag || "",
    assetName: assetData.name || "",
    action: "retired",
    userProfile,
    notes: notes || "Asset retired.",
  });
}

/**
 * deleteAsset — permanently delete an asset document.
 */
export async function deleteAsset(assetId, userProfile = null) {
  const snap = await getDocs(
    query(collection(db, "assets"), where("__name__", "==", assetId))
  );
  const assetData = snap.docs[0]?.data() || {};

  await deleteDoc(doc(db, "assets", assetId));

  await writeAssetHistory({
    assetId,
    assetTag: assetData.assetTag || "",
    assetName: assetData.name || "",
    action: "deleted",
    userProfile,
    notes: "Asset permanently deleted.",
  });
}
